<template>
  <div class="ai-chat-container" :class="{ 'dark': isDark }">
    <!-- Chat Header -->
    <div class="ai-chat-header">
      <div class="ai-chat-title">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M9.5 2A2.5 2.5 0 0 1 12 4.5c0 .74-.4 1.39-1 1.73V7h3a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h3V6.23c-.6-.34-1-.99-1-1.73a2.5 2.5 0 0 1 2.5-2.5z"/>
          <path d="M9 14h6"/>
          <circle cx="12" cy="11" r="1"/>
        </svg>
        <span>AI Trading Analyst</span>
        <div class="help-icon-wrapper" @mouseenter="showHelpTooltip" @mouseleave="hideHelpTooltip">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="help-icon">
            <circle cx="12" cy="12" r="10"/>
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
          <!-- Help Tooltip -->
          <div v-if="showHelp" class="help-tooltip">
            <div class="help-tooltip-content">
              <h4>How AI Analysis Works</h4>
              
              <div class="help-section">
                <h5>📈 Trend Determination</h5>
                <p><strong>Primary Signal:</strong> Visual Trading (VT) signal indicates direction (BUY/SELL)</p>
                <p><strong>Fallback:</strong> EMA50 alignment when VT signal unavailable</p>
                <p><strong>Classification:</strong> BULLISH/BEARISH based on signal direction</p>
              </div>

              <div class="help-section">
                <h5>🎯 Confidence Score (0-100%)</h5>
                <p>Based on 6 confirmation layers:</p>
                <ul>
                  <li><strong>Trend:</strong> EMA50 aligned with signal direction</li>
                  <li><strong>Momentum:</strong> RSI >55 (BUY) or <45 (SELL)</li>
                  <li><strong>Volume:</strong> Volume ratio ≥1.0 (adequate participation)</li>
                  <li><strong>Structure:</strong> Market structure (HH/HL for BUY, LH/LL for SELL)</li>
                  <li><strong>MACD:</strong> MACD trend aligned with signal</li>
                  <li><strong>Momentum:</strong> Momentum indicator aligned</li>
                </ul>
                <p><strong>Signal Strength:</strong></p>
                <ul>
                  <li>80-100%: Strong (5-6 confirmations)</li>
                  <li>50-79%: Valid (3-4 confirmations)</li>
                  <li>20-49%: Weak (1-2 confirmations)</li>
                  <li>0-19%: Bias only (0 confirmations)</li>
                </ul>
              </div>

              <div class="help-section">
                <h5>🎯 Alignment Score (0-5)</h5>
                <p>Multi-timeframe alignment across 5m, 30m, 1H, 4H, 1D:</p>
                <ul>
                  <li><strong>5:</strong> All timeframes aligned (highest confidence)</li>
                  <li><strong>4-3:</strong> Most timeframes aligned (good confidence)</li>
                  <li><strong>2:</strong> Mixed signals (low confidence)</li>
                  <li><strong>0:</strong> No timeframe data available</li>
                </ul>
              </div>

              <div class="help-section disclaimer">
                <h5>⚠️ Important Disclaimer</h5>
                <p>This AI analysis is for informational purposes only. The calculations are based on technical indicators and historical data patterns. <strong>You are solely responsible for your trading decisions.</strong> Always conduct your own research, consider your risk tolerance, and never trade with money you cannot afford to lose.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="ai-chat-credits" v-if="userId">
        <span class="credits-label">Credits</span>
        <span class="credits-value">{{ credits.monthlyAllocation - credits.remaining }}/{{ credits.monthlyAllocation }}</span>
      </div>
      <div class="ai-chat-header-actions">
        <button class="ai-chat-btn-icon" @click="toggleExpand" :title="isExpanded ? 'Switch to inline mode' : 'Switch to float mode'">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <!-- Float mode icon (expanded) - floating panel -->
            <rect v-if="isExpanded" x="3" y="3" width="18" height="18" rx="2" ry="2"/>
            <line v-if="isExpanded" x1="9" y1="9" x2="15" y2="9"/>
            <line v-if="isExpanded" x1="9" y1="15" x2="15" y2="15"/>
            <!-- Inline mode icon (collapsed) - docked window -->
            <g v-else><rect x="2" y="4" width="20" height="16" rx="2" ry="2"/>
            <line x1="2" y1="9" x2="22" y2="9"/>
            <line x1="2" y1="15" x2="22" y2="15"/></g>
          </svg>
        </button>
        <button class="ai-chat-btn-icon" @click="clearHistory" title="Clear chat">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="3 6 5 6 21 6"/>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
          </svg>
        </button>
        <button class="ai-chat-btn-icon" @click="closeChat" title="Close">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- Context Bar -->
    <div class="ai-chat-context" v-if="tradingContext.symbol !== 'N/A'">
      <div class="context-item">
        <span class="context-label">Symbol</span>
        <span class="context-value">{{ tradingContext.symbol }}</span>
      </div>
      <div class="context-item">
        <span class="context-label">Price</span>
        <span class="context-value" :class="getTrendColorClass()">{{ formatPrice(tradingContext.currentPrice) }}</span>
      </div>
      <div class="context-item">
        <span class="context-label">Timeframe</span>
        <span class="context-value">{{ tradingContext.timeframe }}</span>
      </div>
      <div class="context-item confidence-context">
        <span class="context-label">Confidence</span>
        <div class="confidence-progress">
          <div class="confidence-track">
            <div class="confidence-fill" 
                 :style="{ width: (tradingContext.allTimeframeData?.[tradingContext.timeframe]?.confidence || 0) + '%', backgroundColor: getConfidenceColor(tradingContext.allTimeframeData?.[tradingContext.timeframe]?.confidence || 0) }">
            </div>
          </div>
          <span class="confidence-value" :style="{ color: getConfidenceColor(tradingContext.allTimeframeData?.[tradingContext.timeframe]?.confidence || 0) }">
            {{ tradingContext.allTimeframeData?.[tradingContext.timeframe]?.confidence || 0 }}%
          </span>
        </div>
      </div>
    </div>

    <!-- Messages Container -->
    <div class="ai-chat-messages" ref="messagesContainer">
      <div v-if="messages.length === 0" class="ai-chat-empty">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <path d="M12 16v-4"/>
          <path d="M12 8h.01"/>
        </svg>
        <p class="empty-title">Start a conversation</p>
        <p class="empty-subtitle">Ask about market trends, signals, or get trading insights</p>
      </div>

      <div v-for="message in messages" :key="message.id" 
           class="ai-chat-message" 
           :class="{ 'user': message.role === 'user', 'assistant': message.role === 'assistant' }">
        <div class="message-header">
          <span class="message-role">{{ message.role === 'user' ? 'You' : 'AI Analyst' }}</span>
          <span class="message-time" v-if="message.timestamp">{{ formatTime(message.timestamp) }}</span>
        </div>

        <!-- Message Content -->
        <div class="message-content" v-html="renderMarkdown(message.content)"></div>
        
        <!-- Message Actions -->
        <div class="message-actions" v-if="message.role === 'assistant'">
          <button class="message-action-btn" @click="likeMessage(message.id)" :class="{ 'active': isMessageLiked(message.id) }" data-tooltip="Like">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/>
            </svg>
          </button>
          <button class="message-action-btn" @click="dislikeMessage(message.id)" :class="{ 'active': isMessageDisliked(message.id) }" data-tooltip="Dislike">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7L2.34 13a2 2 0 0 0 2 2.3z"/>
              <path d="M17 2h3a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2h-3"/>
            </svg>
          </button>
          <button class="message-action-btn" @click="copyMessage(message.content)" data-tooltip="Copy">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
            </svg>
          </button>
          <button class="message-action-btn" @click="shareMessage(message)" data-tooltip="Share">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="18" cy="5" r="3"/>
              <circle cx="6" cy="12" r="3"/>
              <circle cx="18" cy="19" r="3"/>
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
            </svg>
          </button>
        </div>
      </div>

      <!-- Loading Indicator -->
      <div v-if="loading" class="ai-chat-message assistant loading">
        <div class="loading-indicator">
          <div class="loading-dot"></div>
          <div class="loading-dot"></div>
          <div class="loading-dot"></div>
        </div>
        <span class="loading-text">Analyzing...</span>
      </div>
    </div>

    <!-- Suggestions -->
    <div class="ai-chat-suggestions" v-if="messages.length === 0 || showSuggestions">
      <button v-for="(suggestion, index) in promptSuggestions" :key="index" 
              class="suggestion-chip" 
              @click="useSuggestion(suggestion)">
        {{ suggestion }}
      </button>
    </div>

    <!-- Attachments Area -->
    <div class="ai-chat-attachments" v-if="attachments.length > 0">
      <div class="attachments-container">
        <div v-for="attachment in attachments" :key="attachment.id" class="attachment-item">
          <div class="attachment-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
              <polyline points="10 9 9 9 8 9"/>
            </svg>
          </div>
          <span class="attachment-name">{{ attachment.name }}</span>
          <div class="attachment-status">
            <div v-if="loading && attachment.name.startsWith('market-data-')" class="attachment-sending">
              <div class="sending-dots">
                <div class="dot"></div>
                <div class="dot"></div>
                <div class="dot"></div>
              </div>
              <span class="sending-text">Sending to AI...</span>
            </div>
            <div v-else-if="!attachment.name.startsWith('market-data-')" class="attachment-ready">
              <div class="ready-indicator"></div>
              <span class="ready-text">Ready</span>
            </div>
            <div v-else class="attachment-ready">
              <div class="ready-indicator"></div>
              <span class="ready-text">Market Data</span>
            </div>
          </div>
          <button class="attachment-remove" @click="removeAttachment(attachment.id)" title="Remove attachment">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Input Area -->
    <div class="ai-chat-input-area">
      <div class="ai-chat-input-row">
        <input 
          type="text" 
          v-model="inputText" 
          @keyup.enter="sendMessage"
          :disabled="loading"
          placeholder="Ask about market analysis, trends, signals..."
          class="ai-chat-input"
          ref="inputField"
        />
        <button 
          class="ai-chat-send-btn" 
          @click="sendMessage" 
          :disabled="!inputText.trim() || loading"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="22" y1="2" x2="11" y2="13"/>
            <polygon points="22 2 15 22 11 13 2 9 22 2"/>
          </svg>
        </button>
      </div>
      <div class="ai-chat-footer">
        <button class="summarize-btn" @click="summarizeChat" v-if="messages.length > 4" :disabled="summarizing">
          {{ summarizing ? 'Summarizing...' : 'Summarize' }}
        </button>
      </div>
    </div>

    <!-- Summary Modal -->
    <div v-if="showSummary" class="ai-chat-modal" @click="showSummary = false">
      <div class="ai-chat-modal-content" @click.stop>
        <div class="modal-header">
          <h3>Chat Summary</h3>
          <button class="close-btn" @click="showSummary = false">&times;</button>
        </div>
        <div class="modal-body" v-html="renderMarkdown(summary)"></div>
      </div>
    </div>

    <!-- Clear History Confirmation Modal -->
    <div v-if="showClearConfirmModal" class="ai-chat-modal" @click="showClearConfirmModal = false">
      <div class="ai-chat-modal-content" @click.stop>
        <div class="modal-header">
          <h3>Clear Chat History</h3>
          <button class="close-btn" @click="showClearConfirmModal = false">&times;</button>
        </div>
        <div class="modal-body">
          <p>Are you sure you want to clear all chat history? This action cannot be undone.</p>
          <div class="modal-actions">
            <button class="modal-btn modal-btn-cancel" @click="showClearConfirmModal = false">
              Cancel
            </button>
            <button class="modal-btn modal-btn-confirm" @click="confirmClearHistory">
              Clear History
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AIChatService from '../services/ai_chat_service.js';
import MiniChart from './MiniChart.vue';

export default {
  name: 'AIChat',
  components: {
    MiniChart
  },
  props: {
    tradingContext: {
      type: Object,
      default: () => ({
        symbol: 'N/A',
        exchange: 'N/A',
        timeframe: 'N/A',
        currentPrice: 'N/A',
        marketData: {},
        technicalIndicators: {},
        latestSignal: null,
        allTimeframeData: {},
        alignmentScore: 0,
        chartConfig: null,
      })
    },
    isDark: {
      type: Boolean,
      default: false
    },
    userId: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      messages: [],
      inputText: '',
      loading: false,
      isExpanded: false,
      showSuggestions: false,
      summarizing: false,
      showSummary: false,
      summary: '',
      showClearConfirmModal: false,
      showHelp: false,
      showMarketDataConfirmModal: false,
      marketDataInfo: {
        estimatedTokens: 0,
        dataSize: 0,
        timeframes: []
      },
      pendingMessage: null,
      credits: {
        remaining: 0,
        monthlyAllocation: 500,
      },
      attachments: [],
      currentSession: {
        symbol: null,
        timeframe: null,
        createdAt: null,
        lastAttachmentTime: null
      },
      messageReactions: {},
      copyFeedback: null,
      promptSuggestions: [
        'What is the current trend?',
        'Should I buy or sell?',
        'What are the key support/resistance levels?',
        'Analyze the risk for this trade',
        'What does the latest signal indicate?',
        'Explain the technical indicators',
      ]
    };
  },
  async mounted() {
    this.showSuggestions = true;
    try {
      await AIChatService.loadChatHistory(
        this.tradingContext.symbol,
        this.tradingContext.exchange
      );
      this.messages = AIChatService.getChatHistory();
      
      // Initialize session and check for auto-attachment
      this.initializeSession();
      
      // Load credits if userId available
      if (this.userId) {
        await this.loadCredits();
      }
      
      // Load message reactions
      this.loadMessageReactions();
    } catch (error) {
      console.error('Failed to load chat history:', error);
    }
  },
  watch: {
    userId(newVal, oldVal) {
      if (newVal && !oldVal) {
        this.loadCredits();
      }
    },
    'tradingContext.symbol'(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.loadChatHistory();
        // Check if session needs to be reset
        this.checkSessionReset();
        // Load message reactions for new symbol
        this.loadMessageReactions();
      }
    },
    'tradingContext.timeframe'(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.checkSessionReset();
      }
    },
    tradingContext: {
      handler(newVal, oldVal) {
      },
      deep: true
    }
  },
  methods: {
    initializeSession() {
      const now = Date.now();
      const symbol = this.tradingContext.symbol;
      const timeframe = this.tradingContext.timeframe;
      
      // Check if we need a new session based on specific rules
      let needsNewSession = false;
      
      // Case 1: First time opening a symbol (no existing session)
      if (!this.currentSession.symbol || !this.currentSession.createdAt) {
        // Check if there's existing chat history - if yes, don't treat as new session
        if (this.messages.length > 0) {
          needsNewSession = false;
          // Update session info to match current context
          this.currentSession.symbol = symbol;
          this.currentSession.timeframe = timeframe;
        } else {
          needsNewSession = true;
        }
      }
      // Case 2: Symbol changed
      else if (this.currentSession.symbol !== symbol) {
        needsNewSession = true;
      }
      // Case 3: Existing market data is older than 24 hours
      else if (this.currentSession.lastAttachmentTime && 
               (now - this.currentSession.lastAttachmentTime) > 24 * 60 * 60 * 1000) {
        needsNewSession = true;
      }
      // Case 4: Session cleared and new message is sent (handled in clearHistory)
      // This case is handled when user clears history
      
      if (needsNewSession) {
        this.currentSession = {
          symbol: symbol,
          timeframe: timeframe,
          createdAt: now,
          lastAttachmentTime: null
        };
        
        // Clear attachments for new session
        this.attachments = [];
        
        // Auto-attach fresh market data for new session
        this.autoAttachMarketData();
      } else {
      }
    },
    
    checkSessionReset() {
      // Only check for symbol changes (timeframe changes don't require new session)
      const symbol = this.tradingContext.symbol;
      
      if (this.currentSession.symbol !== symbol) {
        this.initializeSession();
      }
    },
    
    async autoAttachMarketData() {
      if (this.tradingContext.symbol === 'N/A' || !this.tradingContext.allTimeframeData) {
        console.warn('[AIChat] Skipping auto-attach: invalid symbol or no timeframe data');
        return;
      }
      
      try {
        const marketDataFile = await this.generateMarketDataFile();
        if (marketDataFile) {
          this.addAttachment(marketDataFile);
        } else {
          console.warn('[AIChat] Failed to generate market data file for auto-attach');
        }
      } catch (error) {
        console.error('[AIChat] Failed to auto-attach market data:', error);
      }
    },
    
    async generateMarketDataFile() {
      const symbol = this.tradingContext.symbol;
      
      // Edge case: invalid symbol or timeframe
      if (!symbol || symbol === 'N/A') {
        console.warn('[AIChat] Invalid symbol for market data generation');
        return null;
      }
      
      // Edge case: no timeframe data available
      if (!this.tradingContext.allTimeframeData || Object.keys(this.tradingContext.allTimeframeData).length === 0) {
        console.warn('[AIChat] No timeframe data available for market data generation');
        return null;
      }
      
      const now = new Date();
      const timestamp = now.toISOString().replace(/[:.]/g, '').split('T')[1].substring(0, 6);
      const dateStr = now.toISOString().split('T')[0].replace(/-/g, '');
      
      // Generate filename without specific timeframe since we include all timeframes
      const fileName = `market-data-${symbol}-${dateStr}T${timestamp}.json`;
      
      try {
        // Build market data content
        const context = AIChatService.buildTradingContext({
          symbol: this.tradingContext.symbol,
          exchange: this.tradingContext.exchange,
          timeframe: this.tradingContext.timeframe,
          currentPrice: this.tradingContext.currentPrice,
          instrumentData: this.tradingContext.marketData,
          technicalData: this.tradingContext.technicalIndicators,
          latestSignal: this.tradingContext.latestSignal,
          allTimeframeData: this.tradingContext.allTimeframeData,
          alignmentScore: this.tradingContext.alignmentScore,
          chartConfig: this.tradingContext.chartConfig,
        });
        
        const result = await AIChatService.compressMarketData(context);
        if (!result || !result.data) {
          console.warn('[AIChat] Failed to compress market data');
          return null;
        }
        
        const fileContent = {
          metadata: {
            symbol: symbol,
            generatedAt: now.toISOString(),
            exchange: this.tradingContext.exchange || 'N/A',
            version: '1.0',
            timeframes: Object.keys(this.tradingContext.allTimeframeData),
            primaryTimeframe: this.tradingContext.timeframe
          },
          marketData: result.data
        };
        
        const jsonString = JSON.stringify(fileContent, null, 2);
        
        // Edge case: validate JSON string
        if (!jsonString || jsonString.length === 0) {
          console.warn('[AIChat] Generated empty JSON file');
          return null;
        }
        
        return {
          id: Date.now().toString(),
          name: fileName,
          type: 'application/json',
          content: jsonString,
          size: jsonString.length,
          createdAt: now
        };
      } catch (error) {
        console.error('[AIChat] Error generating market data file:', error);
        return null;
      }
    },
    
    addAttachment(attachment) {
      // Remove existing market data attachments if any
      this.attachments = this.attachments.filter(a => !a.name.startsWith('market-data-'));
      this.attachments.push(attachment);
      this.currentSession.lastAttachmentTime = Date.now();
    },
    
    removeAttachment(attachmentId) {
      this.attachments = this.attachments.filter(a => a.id !== attachmentId);
    },
    
    clearAttachments() {
      this.attachments = [];
    },
    async loadChatHistory() {
      try {
        const history = await AIChatService.loadChatHistory(
          this.tradingContext.symbol,
          this.tradingContext.exchange
        );
        this.messages = history
          .filter(msg => msg.role !== 'system')
          .map(msg => ({
            id: msg.id || Date.now() + Math.random(),
            role: msg.role,
            content: msg.content,
            confidenceScore: msg.confidenceScore || null,
            timestamp: msg.timestamp || new Date().toISOString(),
            chartRequest: msg.chartRequest || null,
          }));
      } catch (error) {
        console.error('Failed to load chat history:', error);
      }
    },

    async sendMessage() {
      const trimmedInput = this.inputText.trim();
 if (!trimmedInput || this.loading) return;

      const userMessage = trimmedInput;
      this.inputText = '';

      this.loading = true;
      this.showSuggestions = false;

      // Generate unique IDs to avoid duplication
      const baseId = Date.now() + Math.random().toString(36).substr(2, 9);
      const userMsgId = baseId + '_user';
      const assistantMsgId = baseId + '_assistant';

        // Add user message immediately for UI responsiveness
      const userMsg = {
        id: userMsgId,
        role: 'user',
        content: userMessage,
        timestamp: new Date().toISOString(),
      };
      this.messages.push(userMsg);
      this.scrollToBottom();

      try {
        // Build current context
        const context = AIChatService.buildTradingContext({
          symbol: this.tradingContext.symbol,
          exchange: this.tradingContext.exchange,
          timeframe: this.tradingContext.timeframe,
          currentPrice: this.tradingContext.currentPrice,
          instrumentData: this.tradingContext.marketData,
          technicalData: this.tradingContext.technicalIndicators,
          latestSignal: this.tradingContext.latestSignal,
          allTimeframeData: this.tradingContext.allTimeframeData,
          alignmentScore: this.tradingContext.alignmentScore,
          chartConfig: this.tradingContext.chartConfig,
        });

        // Prepare attachments for request
        const requestAttachments = this.attachments.map(att => ({
          type: 'file',
          name: att.name,
          mimeType: att.type,
          content: att.content
        }));

        const response = await AIChatService.sendMessageWithAttachments(context, userMessage, requestAttachments, this.userId);

        // Update credits after successful send
        if (this.userId && this.credits.remaining > 0) {
          this.credits.remaining--;
        }

        // Check for chart request in response
        const chartMarkerRegex = /#SHOWCHART-([A-Z0-9_]+)-(\w+)/;
        const chartMatch = response.content.match(chartMarkerRegex);
        let cleanContent = response.content;
        let chartRequest = null;

        if (chartMatch) {
          const [, symbol, timeframe] = chartMatch;
          chartRequest = { symbol, timeframe };
          cleanContent = response.content.replace(chartMarkerRegex, '').trim();
        }

        // Add assistant response
        const assistantMsg = {
          id: assistantMsgId,
          role: 'assistant',
          content: cleanContent,
          confidenceScore: response.confidenceScore,
          timestamp: new Date().toISOString(),
          chartRequest: chartRequest,
        };
        this.messages.push(assistantMsg);

        // Save both messages to service history (prevents duplication on reload)
        // Only add to service history if not already there to avoid duplicates
        const lastUserMsg = AIChatService.chatHistory[AIChatService.chatHistory.length - 2];
        const lastAssistantMsg = AIChatService.chatHistory[AIChatService.chatHistory.length - 1];
        
        if (!lastUserMsg || lastUserMsg.content !== userMessage) {
            AIChatService.chatHistory.push(userMsg);
        }
        if (!lastAssistantMsg || lastAssistantMsg.content !== assistantMsg.content) {
            AIChatService.chatHistory.push(assistantMsg);
        }
        
        await AIChatService.saveChatHistory(AIChatService.chatHistory);
        await AIChatService.saveMessageState();

        // Clear attachments after successful send
        this.clearAttachments();

        this.scrollToBottom();
      } catch (error) {
        console.error('Error sending message:', error);
        this.messages.push({
          id: Date.now() + '_error',
          role: 'assistant',
          content: `**Error:** ${error.message || 'Failed to get AI response. Please check your API key.'}`,
          timestamp: new Date().toISOString(),
          isError: true,
        });
        this.scrollToBottom();
      } finally {
        this.loading = false;
      }
    },

    useSuggestion(suggestion) {
      this.inputText = suggestion;
      this.$refs.inputField.focus();
    },

    async loadCredits() {
      try {
        const credits = await AIChatService.getCredits(this.userId);
        if (credits) {
          this.credits = credits;
        }
      } catch (error) {
        console.error('Failed to load credits:', error);
      }
    },

    async clearHistory() {
      this.showClearConfirmModal = true;
    },
    
    async confirmClearHistory() {
      this.showClearConfirmModal = false;
      try {
        await AIChatService.clearChatHistory();
        this.messages = [];
        this.showSuggestions = true;
        this.clearAttachments();
        
        // Case 4: Session cleared - reset session to trigger new attachment on next message
        this.currentSession = {
          symbol: this.tradingContext.symbol,
          timeframe: this.tradingContext.timeframe,
          createdAt: null,
          lastAttachmentTime: null
        };
        
      } catch (error) {
        console.error('Failed to clear chat history:', error);
      }
    },

    async summarizeChat() {
      this.summarizing = true;
      try {
        // Deduct credit for summarize action
        if (this.userId) {
          try {
            await AIChatService.deductCredit(this.userId);
            if (this.credits.remaining > 0) {
              this.credits.remaining--;
            }
          } catch (creditError) {
            if (creditError.message.includes('No credits remaining')) {
              alert('No AI credits remaining for summary.');
              return;
            }
            throw creditError;
          }
        }

        const summary = await AIChatService.summarizeChat();
        this.summary = summary;
        this.showSummary = true;
      } catch (error) {
        console.error('Failed to summarize chat:', error);
        alert('Failed to summarize chat: ' + error.message);
      } finally {
        this.summarizing = false;
      }
    },

    toggleExpand() {
      this.isExpanded = !this.isExpanded;
      this.$emit('toggle-float-mode');
      this.$nextTick(() => {
        this.scrollToBottom();
      });
    },

    closeChat() {
      this.$emit('close');
    },

    loadChart(chartRequest) {
      this.$emit('load-chart', chartRequest);
    },

    expandChart(chartImage) {
      // Open chart image in a modal or new tab
      window.open(chartImage, '_blank');
    },

    addChartImageToMessage(chartImage, symbol, timeframe) {
      // Add a chart image message to the chat
      this.messages.push({
        id: Date.now() + '_chart',
        role: 'assistant',
        content: `Chart snapshot for ${symbol} (${timeframe}):`,
        timestamp: new Date().toISOString(),
        chartImage: chartImage,
        chartSymbol: symbol,
        chartTimeframe: timeframe,
      });
      this.scrollToBottom();
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },

    formatPrice(price) {
      if (!price || price === 'N/A') return '--';
      if (typeof price === 'number') {
        return price >= 1000 
          ? price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) 
          : price.toFixed(price >= 1 ? 4 : 6);
      }
      return price;
    },

    formatTime(timestamp) {
      if (!timestamp) return '';
      const date = new Date(timestamp);
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    },

    getTrendColorClass() {
      const trend = this.tradingContext.allTimeframeData?.[this.tradingContext.timeframe]?.trend;
      if (trend === 'BULLISH' || trend === 'GREEN') return 'text-bullish';
      if (trend === 'BEARISH' || trend === 'RED') return 'text-bearish';
      return '';
    },

    getConfidenceColor(score) {
      if (score >= 80) return '#22c55e';
      if (score >= 50) return '#f97316';
      return '#ef4444';
    },

    showHelpTooltip() {
      this.showHelp = true;
      this.$nextTick(() => {
        this.positionTooltip();
      });
    },

    hideHelpTooltip() {
      this.showHelp = false;
    },

    positionTooltip() {
      const helpIcon = this.$el.querySelector('.help-icon-wrapper');
      const tooltip = this.$el.querySelector('.help-tooltip');
      
      if (helpIcon && tooltip) {
        const iconRect = helpIcon.getBoundingClientRect();
        const tooltipRect = tooltip.getBoundingClientRect();
        
        // Position tooltip above the icon
        let left = iconRect.left + (iconRect.width / 2) - (tooltipRect.width / 2);
        let top = iconRect.top - tooltipRect.height - 8;
        
        // Ensure tooltip stays within viewport
        if (left < 10) left = 10;
        if (left + tooltipRect.width > window.innerWidth - 10) {
          left = window.innerWidth - tooltipRect.width - 10;
        }
        if (top < 10) {
          top = iconRect.bottom + 8;
        }
        
        tooltip.style.left = left + 'px';
        tooltip.style.top = top + 'px';
        
        // Position arrow - use CSS variable approach
        const arrowLeft = iconRect.left + (iconRect.width / 2) - left;
        tooltip.style.setProperty('--arrow-left', arrowLeft + 'px');
      }
    },

    renderMarkdown(text) {
      // Simple markdown rendering
      if (!text) return '';
      let html = text
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/^## (.*$)/gim, '<h2>$1</h2>')
        .replace(/^### (.*$)/gim, '<h3>$1</h3>')
        .replace(/^# (.*$)/gim, '<h1>$1</h1>')
        .replace(/^- (.*$)/gim, '<li>$1</li>')
        .replace(/`([^`]+)`/g, '<code>$1</code>')
        .replace(/\n/g, '<br>');
      
      // Wrap consecutive li elements in ul
      html = html.replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>');
      
      return html;
    },

    formatBytes(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    // Message Reaction Methods
    likeMessage(messageId) {
      // Remove dislike if it exists
      if (this.messageReactions[messageId] === 'dislike') {
        this.$delete(this.messageReactions, messageId);
      }
      // Toggle like
      if (this.messageReactions[messageId] === 'like') {
        this.$delete(this.messageReactions, messageId);
      } else {
        this.$set(this.messageReactions, messageId, 'like');
      }
      this.saveMessageReactions();
    },

    dislikeMessage(messageId) {
      // Remove like if it exists
      if (this.messageReactions[messageId] === 'like') {
        this.$delete(this.messageReactions, messageId);
      }
      // Toggle dislike
      if (this.messageReactions[messageId] === 'dislike') {
        this.$delete(this.messageReactions, messageId);
      } else {
        this.$set(this.messageReactions, messageId, 'dislike');
      }
      this.saveMessageReactions();
    },

    isMessageLiked(messageId) {
      return this.messageReactions[messageId] === 'like';
    },

    isMessageDisliked(messageId) {
      return this.messageReactions[messageId] === 'dislike';
    },

    async copyMessage(content) {
      try {
        // Strip HTML tags for plain text copy
        const plainText = content.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ');
        await navigator.clipboard.writeText(plainText);
        
        // Show feedback via tooltip
        this.showCopyFeedback('Copied!');
      } catch (error) {
        console.error('Failed to copy message:', error);
        // Fallback for older browsers
        this.fallbackCopyToClipboard(content);
      }
    },

    fallbackCopyToClipboard(content) {
      const plainText = content.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ');
      const textArea = document.createElement('textarea');
      textArea.value = plainText;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        document.execCommand('copy');
        this.showCopyFeedback('Copied!');
      } catch (error) {
        console.error('Fallback copy failed:', error);
        this.showCopyFeedback('Copy failed');
      }
      
      document.body.removeChild(textArea);
    },

    showCopyFeedback(message) {
      // Set the tooltip on the copy button
      const copyButton = event.target.closest('.message-action-btn');
      if (copyButton) {
        copyButton.setAttribute('data-tooltip', message);
        setTimeout(() => {
          copyButton.removeAttribute('data-tooltip');
        }, 2000);
      }
    },

    shareMessage(message) {
      const shareText = this.stripHtml(message.content);
      const shareData = {
        title: 'AI Trading Analysis',
        text: shareText,
        url: window.location.href
      };

      if (navigator.share) {
        // Use Web Share API if available
        navigator.share(shareData)
          .then(() => {})
          .catch((error) => {});
      } else {
        // Fallback: Copy to clipboard
        this.copyMessage(shareText);
        alert('Message copied to clipboard. You can now share it manually.');
      }
    },

    stripHtml(html) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      return tempDiv.textContent || tempDiv.innerText || '';
    },

    saveMessageReactions() {
      try {
        const key = `ai_chat_reactions_${this.tradingContext.symbol}_${this.tradingContext.exchange || 'default'}`;
        localStorage.setItem(key, JSON.stringify(this.messageReactions));
      } catch (error) {
        console.error('Failed to save message reactions:', error);
      }
    },

    loadMessageReactions() {
      try {
        const key = `ai_chat_reactions_${this.tradingContext.symbol}_${this.tradingContext.exchange || 'default'}`;
        const reactions = localStorage.getItem(key);
        if (reactions) {
          this.messageReactions = JSON.parse(reactions);
        }
      } catch (error) {
        console.error('Failed to load message reactions:', error);
        this.messageReactions = {};
      }
    }
  }
};
</script>

<style scoped>
.ai-chat-container {
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-left: 1px solid #e0e3eb;
  height: 100%;
  max-height: 100%;
  font-family: -apple-system, BlinkMacSystemFont, 'Trebuchet MS', Roboto, Ubuntu, sans-serif;
  overflow: hidden;
}

.ai-chat-container.dark {
  background: rgb(18, 24, 38);
  border-left-color: #2f3240;
}

/* Header */
.ai-chat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-bottom: 1px solid #e0e3eb;
  background: #ffffff;
  color: #131722;
  flex-shrink: 0;
}

.ai-chat-container.dark .ai-chat-header {
  background: rgb(18, 24, 38);
  border-bottom-color: #2f3240;
  color: #d1d4dc;
}

.ai-chat-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 14px;
}

.ai-chat-title svg {
  color: #22c55e;
}

/* Help Icon */
.help-icon-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
  cursor: help;
}

.help-icon {
  color: #787b86;
  transition: color 0.15s;
}

.help-icon:hover {
  color: #2962ff;
}

.ai-chat-container.dark .help-icon {
  color: #787b86;
}

.ai-chat-container.dark .help-icon:hover {
  color: #5c9aff;
}

/* Help Tooltip */
.help-tooltip {
  position: fixed;
  z-index: 99999;
  width: 400px;
  max-width: 90vw;
}

.help-tooltip::before {
  content: '';
  position: absolute;
  top: -6px;
  left: var(--arrow-left, 50%);
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-bottom: 6px solid #ffffff;
}

.ai-chat-container.dark .help-tooltip::before {
  border-bottom-color: rgb(30, 36, 50);
}

.help-tooltip-content {
  background: #ffffff;
  border: 1px solid #e0e3eb;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  max-height: 70vh;
  overflow-y: auto;
  font-size: 12px;
  line-height: 1.4;
}

.ai-chat-container.dark .help-tooltip-content {
  background: rgb(30, 36, 50);
  border-color: #2f3240;
  color: #d1d4dc;
}

.help-tooltip-content h4 {
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: #131722;
  border-bottom: 1px solid #e0e3eb;
  padding-bottom: 8px;
}

.ai-chat-container.dark .help-tooltip-content h4 {
  color: #d1d4dc;
  border-bottom-color: #2f3240;
}

.help-section {
  margin-bottom: 16px;
}

.help-section:last-child {
  margin-bottom: 0;
}

.help-section h5 {
  margin: 0 0 8px 0;
  font-size: 13px;
  font-weight: 600;
  color: #2962ff;
}

.ai-chat-container.dark .help-section h5 {
  color: #5c9aff;
}

.help-section p {
  margin: 0 0 8px 0;
  color: #4b5563;
}

.ai-chat-container.dark .help-section p {
  color: #9ca3af;
}

.help-section ul {
  margin: 8px 0;
  padding-left: 16px;
  color: #4b5563;
}

.ai-chat-container.dark .help-section ul {
  color: #9ca3af;
}

.help-section li {
  margin-bottom: 4px;
  line-height: 1.3;
}

.help-section.disclaimer {
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  padding: 12px;
  margin-top: 16px;
}

.ai-chat-container.dark .help-section.disclaimer {
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.3);
}

.help-section.disclaimer h5 {
  color: #dc2626;
}

.ai-chat-container.dark .help-section.disclaimer h5 {
  color: #ef4444;
}

.help-section.disclaimer p {
  color: #991b1b;
}

.ai-chat-container.dark .help-section.disclaimer p {
  color: #fca5a5;
}

.ai-chat-credits {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-left: auto;
  margin-right: 12px;
}

.credits-label {
  font-size: 10px;
  color: #787b86;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.credits-value {
  font-size: 12px;
  font-weight: 600;
  color: #2962ff;
}

.ai-chat-container.dark .credits-value {
  color: #5c9aff;
}

.ai-chat-header-actions {
  display: flex;
  gap: 4px;
}

.ai-chat-btn-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  color: #787b86;
  transition: all 0.15s;
}

.ai-chat-btn-icon:hover {
  background: rgba(0, 0, 0, 0.06);
  color: #131722;
}

.ai-chat-container.dark .ai-chat-btn-icon {
  color: #787b86;
}

.ai-chat-container.dark .ai-chat-btn-icon:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #d1d4dc;
}

/* Context Bar */
.ai-chat-context {
  display: flex;
  gap: 16px;
  padding: 8px 12px;
  background: #f8f9fd;
  border-bottom: 1px solid #e0e3eb;
  flex-shrink: 0;
}

.ai-chat-container.dark .ai-chat-context {
  background: rgb(22, 28, 42);
  border-bottom-color: #2f3240;
}

.context-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.context-label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6b7280;
}

.ai-chat-container.dark .context-label {
  color: #9ca3af;
}

.context-value {
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
}

.ai-chat-container.dark .context-value {
  color: #e5e7eb;
}

.context-value.text-bullish {
  color: #22c55e;
}

.context-value.text-bearish {
  color: #ef4444;
}

/* Confidence Progress in Context Bar */
.confidence-context {
  min-width: 120px;
}

.confidence-progress {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.confidence-track {
  flex: 1;
  height: 6px;
  background: #e5e7eb;
  border-radius: 3px;
  overflow: hidden;
}

.ai-chat-container.dark .confidence-track {
  background: #374151;
}

.confidence-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.3s ease, background-color 0.3s ease;
}

.confidence-value {
  font-size: 11px;
  font-weight: 600;
  min-width: 35px;
  text-align: right;
}

/* Messages */
.ai-chat-messages {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: #ffffff;
  min-height: 0;
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
}

.ai-chat-container.dark .ai-chat-messages {
  background: rgb(18, 24, 38);
}

.ai-chat-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #9ca3af;
  text-align: center;
}

.ai-chat-empty svg {
  margin-bottom: 16px;
  opacity: 0.5;
}

.empty-title {
  font-weight: 600;
  color: #6b7280;
  margin: 0 0 4px;
}

.empty-subtitle {
  font-size: 13px;
  color: #9ca3af;
  margin: 0;
}

.ai-chat-message {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-width: 90%;
}

.ai-chat-message.user {
  align-self: flex-end;
}

.ai-chat-message.assistant {
  align-self: flex-start;
}

.message-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
}

.message-role {
  font-weight: 600;
}

.ai-chat-message.user .message-role {
  color: #0366d6;
}

.ai-chat-message.assistant .message-role {
  color: #22c55e;
}

.message-time {
  color: #9ca3af;
  font-size: 11px;
}

/* Confidence Bar */
.confidence-bar {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 6px;
  padding: 8px 12px;
  margin-bottom: 4px;
}

.ai-chat-container.dark .confidence-bar {
  background: rgba(255, 255, 255, 0.05);
}

.confidence-bar-label {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 11px;
  margin-bottom: 4px;
  color: #6b7280;
}

.confidence-bar-track {
  height: 4px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 2px;
  overflow: hidden;
}

.ai-chat-container.dark .confidence-bar-track {
  background: rgba(255, 255, 255, 0.1);
}

.confidence-bar-fill {
  height: 100%;
  border-radius: 2px;
  transition: width 0.3s ease;
}

/* Chart Request */
.chart-request {
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border: 1px solid #86efac;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 6px;
}

.ai-chat-container.dark .chart-request {
  background: linear-gradient(135deg, #064e3b 0%, #065f46 100%);
  border-color: #22c55e;
}

.chart-request-header {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 12px;
  font-weight: 600;
}

.chart-symbol {
  color: #166534;
}

.ai-chat-container.dark .chart-symbol {
  color: #86efac;
}

.chart-timeframe {
  color: #22c55e;
  background: rgba(34, 197, 94, 0.1);
  padding: 2px 6px;
  border-radius: 4px;
}

.chart-load-btn {
  width: 100%;
  padding: 6px 12px;
  background: #22c55e;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}

.chart-load-btn:hover {
  background: #16a34a;
}

.chart-show-btn {
  width: 100%;
  padding: 6px 12px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
  margin-top: 6px;
}

.chart-show-btn:hover {
  background: #2563eb;
}

/* Chart Image Display */
.chart-image-container {
  background: #f8f9fd;
  border: 1px solid #e0e3eb;
  border-radius: 8px;
  padding: 8px;
  margin-bottom: 6px;
  text-align: center;
}

.ai-chat-container.dark .chart-image-container {
  background: rgb(30, 36, 50);
  border-color: #2f3240;
}

.chart-image {
  max-width: 100%;
  border-radius: 4px;
  cursor: pointer;
  transition: transform 0.2s;
}

.chart-image:hover {
  transform: scale(1.02);
}

.chart-image-caption {
  font-size: 11px;
  color: #6b7280;
  margin-top: 6px;
}

.ai-chat-container.dark .chart-image-caption {
  color: #9ca3af;
}

/* Message Content */
.message-content {
  background: #f8f9fd;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 13px;
  line-height: 1.5;
  color: #131722;
}

.ai-chat-container.dark .message-content {
  background: rgb(30, 36, 50);
  color: #d1d4dc;
}

.ai-chat-message.user .message-content {
  background: #2962ff;
  color: white;
}

.ai-chat-message.assistant .message-content {
  border-top-left-radius: 4px;
}

.ai-chat-message.user .message-content {
  border-top-right-radius: 4px;
}

.message-content :deep(h1),
.message-content :deep(h2),
.message-content :deep(h3) {
  margin: 12px 0 8px;
  font-weight: 600;
}

.message-content :deep(h1) {
  font-size: 16px;
}

.message-content :deep(h2) {
  font-size: 14px;
}

.message-content :deep(h3) {
  font-size: 13px;
}

.message-content :deep(ul) {
  margin: 8px 0;
  padding-left: 20px;
}

.message-content :deep(li) {
  margin: 4px 0;
}

.message-content :deep(code) {
  background: rgba(0, 0, 0, 0.05);
  padding: 2px 4px;
  border-radius: 3px;
  font-family: 'DM Mono', monospace;
  font-size: 12px;
}

.ai-chat-container.dark .message-content :deep(code) {
  background: rgba(255, 255, 255, 0.1);
}

.message-content :deep(strong) {
  font-weight: 600;
}

/* Loading Indicator */
.loading-indicator {
  display: flex;
  gap: 4px;
  align-items: center;
  justify-content: center;
  padding: 8px;
}

.loading-dot {
  width: 6px;
  height: 6px;
  background: #22c55e;
  border-radius: 50%;
  animation: bounce 1.4s infinite ease-in-out both;
}

.loading-dot:nth-child(1) {
  animation-delay: -0.32s;
}

.loading-dot:nth-child(2) {
  animation-delay: -0.16s;
}

@keyframes bounce {
  0%, 80%, 100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
}

.loading-text {
  font-size: 12px;
  color: #9ca3af;
  margin-left: 8px;
}

/* Suggestions - Single line with horizontal scroll */
.ai-chat-suggestions {
  display: flex;
  flex-wrap: nowrap;
  gap: 8px;
  padding: 8px 12px;
  border-top: 1px solid #e5e7eb;
  background: #fafafa;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: thin;
  -webkit-overflow-scrolling: touch;
}

.ai-chat-suggestions::-webkit-scrollbar {
  height: 4px;
}

.ai-chat-suggestions::-webkit-scrollbar-track {
  background: transparent;
}

.ai-chat-suggestions::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 2px;
}

.ai-chat-container.dark .ai-chat-suggestions {
  background: #1a1f2e;
  border-top-color: #2f3240;
}

.ai-chat-container.dark .ai-chat-suggestions::-webkit-scrollbar-thumb {
  background: #4b5563;
}

.suggestion-chip {
  padding: 6px 12px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  font-size: 12px;
  color: #4b5563;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
  flex-shrink: 0;
}

.ai-chat-container.dark .suggestion-chip {
  background: #2f3240;
  border-color: #3f4555;
  color: #e5e7eb;
}

.suggestion-chip:hover {
  border-color: #22c55e;
  color: #22c55e;
}

/* Attachments */
.ai-chat-attachments {
  border-bottom: 1px solid #e0e3eb;
  background: #f8f9fd;
  padding: 8px 12px;
}

.ai-chat-container.dark .ai-chat-attachments {
  background: rgb(22, 28, 42);
  border-bottom-color: #2f3240;
}

.attachments-container {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 4px 0;
  scrollbar-width: thin;
  -webkit-overflow-scrolling: touch;
}

.attachments-container::-webkit-scrollbar {
  height: 4px;
}

.attachments-container::-webkit-scrollbar-track {
  background: transparent;
}

.attachments-container::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 2px;
}

.ai-chat-container.dark .attachments-container::-webkit-scrollbar-thumb {
  background: #4b5563;
}

.attachment-item {
  display: flex;
  align-items: center;
  gap: 8px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 6px 8px;
  min-width: 0;
  flex-shrink: 0;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.ai-chat-container.dark .attachment-item {
  background: #2f3240;
  border-color: #3f4555;
  color: #e5e7eb;
}

.attachment-icon {
  color: #22c55e;
  flex-shrink: 0;
}

.attachment-name {
  font-size: 12px;
  font-weight: 500;
  color: #374151;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
  flex: 1;
}

.ai-chat-container.dark .attachment-name {
  color: #e5e7eb;
}

.attachment-status {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  flex-shrink: 0;
}

.attachment-sending {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #059669;
}

.sending-dots {
  display: flex;
  gap: 2px;
}

.sending-dots .dot {
  width: 4px;
  height: 4px;
  background: #059669;
  border-radius: 50%;
  animation: sending-pulse 1.4s infinite ease-in-out both;
}

.sending-dots .dot:nth-child(1) {
  animation-delay: -0.32s;
}

.sending-dots .dot:nth-child(2) {
  animation-delay: -0.16s;
}

@keyframes sending-pulse {
  0%, 80%, 100% {
    transform: scale(0);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

.sending-text {
  color: #059669;
  font-weight: 500;
}

.attachment-ready {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #22c55e;
}

.ready-indicator {
  width: 8px;
  height: 8px;
  background: #22c55e;
  border-radius: 50%;
  flex-shrink: 0;
}

.ready-text {
  color: #22c55e;
  font-weight: 500;
}

.ai-chat-container.dark .sending-text {
  color: #10b981;
}

.ai-chat-container.dark .ready-text {
  color: #10b981;
}

.ai-chat-container.dark .ready-indicator {
  background: #10b981;
}

.ai-chat-container.dark .sending-dots .dot {
  background: #10b981;
}

.attachment-remove {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  color: #6b7280;
  transition: all 0.15s;
  flex-shrink: 0;
}

.attachment-remove:hover {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.ai-chat-container.dark .attachment-remove {
  color: #9ca3af;
}

.ai-chat-container.dark .attachment-remove:hover {
  background: rgba(239, 68, 68, 0.2);
  color: #f87171;
}

/* Input Area */
.ai-chat-input-area {
  padding: 10px 12px;
  border-top: 1px solid #e0e3eb;
  background: #ffffff;
  flex-shrink: 0;
}

.ai-chat-container.dark .ai-chat-input-area {
  background: rgb(18, 24, 38);
  border-top-color: #2f3240;
}

.ai-chat-input-row {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}

.ai-chat-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #e0e3eb;
  border-radius: 4px;
  font-size: 13px;
  resize: none;
  outline: none;
  transition: border-color 0.15s;
  font-family: inherit;
  background: #ffffff;
  color: #131722;
}

.ai-chat-input:focus {
  border-color: #2962ff;
}

.ai-chat-container.dark .ai-chat-input {
  background: rgb(30, 36, 50);
  border-color: #2f3240;
  color: #d1d4dc;
}

.ai-chat-container.dark .ai-chat-input:focus {
  border-color: #2962ff;
}

.ai-chat-input::placeholder {
  color: #9ca3af;
}

.ai-chat-send-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: #22c55e;
  border: none;
  border-radius: 8px;
  color: white;
  cursor: pointer;
  transition: background 0.15s;
  flex-shrink: 0;
}

.ai-chat-send-btn:hover:not(:disabled) {
  background: #16a34a;
}

.ai-chat-send-btn:disabled {
  background: #d1d5db;
  cursor: not-allowed;
}

.ai-chat-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 11px;
}

.ai-model-info {
  color: #9ca3af;
}

.summarize-btn {
  padding: 4px 10px;
  background: transparent;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  font-size: 11px;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.15s;
}

.summarize-btn:hover:not(:disabled) {
  border-color: #22c55e;
  color: #22c55e;
}

.summarize-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Modal */
.ai-chat-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.ai-chat-modal-content {
  background: white;
  border-radius: 12px;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
}

.ai-chat-container.dark .ai-chat-modal-content {
  background: #1a1f2e;
  color: #e5e7eb;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
}

.ai-chat-container.dark .modal-header {
  border-bottom-color: #2f3240;
}

.modal-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
}

/* Modal Buttons */
.modal-actions {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    margin-top: 20px;
}

.modal-btn {
    padding: 8px 16px;
    border: none;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s;
}

.modal-btn-cancel {
    background: #6b7280;
    color: white;
    border: 1px solid #6b7280;
}

.modal-btn-cancel:hover {
    background: #5a6268;
}

.modal-btn-confirm {
    background: #dc2626;
    color: white;
    border: 1px solid #dc2626;
}

.modal-btn-confirm:hover {
    background: #b91c1c;
}

/* Market Data Info */
.market-data-info {
    background: #f8f9fd;
    border: 1px solid #e0e3eb;
    border-radius: 8px;
    padding: 16px;
    margin: 16px 0;
}

.ai-chat-container.dark .market-data-info {
    background: rgb(30, 36, 50);
    border-color: #2f3240;
}

.info-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid #e5e7eb;
}

.info-item:last-child {
    border-bottom: none;
}

.ai-chat-container.dark .info-item {
    border-bottom-color: #374151;
}

.info-label {
    font-size: 14px;
    font-weight: 500;
    color: #374151;
}

.ai-chat-container.dark .info-label {
    color: #9ca3af;
}

.info-value {
    font-size: 14px;
    font-weight: 600;
    color: #059669;
}

.modal-note {
    font-size: 12px;
    color: #6b7280;
    font-style: italic;
    margin: 12px 0;
}

.ai-chat-container.dark .modal-note {
    color: #9ca3af;
}

.ai-chat-container.dark .modal-btn {
    color: white;
}

.ai-chat-container.dark .modal-btn-cancel {
    background: #ef4444;
    border-color: #ef4444;
}

.ai-chat-container.dark .modal-btn-cancel:hover {
    background: #dc2626;
}

.ai-chat-container.dark .modal-btn-confirm {
    background: #059669;
    border-color: #059669;
}

.ai-chat-container.dark .modal-btn-confirm:hover {
    background: #047857;
}

.close-btn:hover {
  color: #374151;
}

.modal-body {
  padding: 20px;
  font-size: 13px;
  line-height: 1.6;
}

/* Scrollbar */
.ai-chat-messages::-webkit-scrollbar {
  width: 6px;
}

.ai-chat-messages::-webkit-scrollbar-track {
  background: transparent;
}

.ai-chat-messages::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 3px;
}

.ai-chat-messages::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}

.ai-chat-container.dark .ai-chat-messages::-webkit-scrollbar-thumb {
  background: #3f4555;
}

.ai-chat-container.dark .ai-chat-messages::-webkit-scrollbar-thumb:hover {
  background: #5a5f70;
}

/* Message Actions */
.message-actions {
  display: flex;
  gap: 4px;
  margin-top: 8px;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.ai-chat-message:hover .message-actions {
  opacity: 1;
}

.message-action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

.message-action-btn:hover {
  background: rgba(0, 0, 0, 0.05);
  color: #374151;
}

.ai-chat-container.dark .message-action-btn {
  color: #9ca3af;
}

.ai-chat-container.dark .message-action-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #e5e7eb;
}

.message-action-btn.active {
  color: #22c55e;
  background: rgba(34, 197, 94, 0.1);
}

.message-action-btn.active:hover {
  background: rgba(34, 197, 94, 0.2);
}

.ai-chat-container.dark .message-action-btn.active {
  color: #86efac;
  background: rgba(134, 239, 172, 0.1);
}

.ai-chat-container.dark .message-action-btn.active:hover {
  background: rgba(134, 239, 172, 0.2);
}

/* Tooltip styling */
.message-action-btn::after {
  content: attr(data-tooltip);
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: #374151;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
  margin-bottom: 4px;
  z-index: 1000;
}

.message-action-btn:hover::after {
  opacity: 1;
}

.message-action-btn[data-tooltip="Copied!"]::after {
  background: #22c55e;
}

.message-action-btn[data-tooltip="Copy failed"]::after {
  background: #ef4444;
}

.ai-chat-container.dark .message-action-btn::after {
  background: #d1d4dc;
  color: #1f2937;
}

.ai-chat-container.dark .message-action-btn[data-tooltip="Copied!"]::after {
  background: #86efac;
  color: #064e3b;
}

.ai-chat-container.dark .message-action-btn[data-tooltip="Copy failed"]::after {
  background: #fca5a5;
  color: #7f1d1d;
}
</style>
